import { useState, useEffect } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Download, Phone, RefreshCw, Search, Globe, Users, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PhoneNumberAdminService } from "@/services/admin/PhoneNumberAdminService";
import type { PhoneNumberFilters, PhoneNumberWithDetails } from "@/types/phone-numbers";
import {
  usePhoneNumbers,
  usePhoneNumberStats,
  usePhoneNumberCountryCodes,
} from "./hooks/usePhoneNumbers";
import {
  getStatusBadge,
  getTypeBadge,
  formatPhoneNumber,
  getCountryName,
  getCountryFlag,
} from "./utils/phoneNumberUtils";
import PortingTaskDrawer from "./components/PortingTaskDrawer";

const PhoneNumbersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<PhoneNumberFilters>({});
  const [page, setPage] = useState(1);
  const [quickSearch, setQuickSearch] = useState("");
  const [activeView, setActiveView] = useState<"all" | "porting">("all");
  const [selectedPortingTask, setSelectedPortingTask] = useState<PhoneNumberWithDetails | null>(null);

  const effectiveFilters = activeView === "porting" ? { ...filters, portingOnly: true } : filters;
  const { data: phoneData, isLoading, refetch: refetchPhoneNumbers } = usePhoneNumbers(effectiveFilters, page, 50);

  // Auto-open drawer when ?id= is present in the URL
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || !phoneData?.data) return;
    const match = phoneData.data.find((p) => String(p.id) === id);
    if (match) {
      setSelectedPortingTask(match);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, phoneData?.data]);
  const { data: stats, refetch: refetchStats } = usePhoneNumberStats(filters);
  const { data: countryCodes } = usePhoneNumberCountryCodes();

  const handleExport = async () => {
    try {
      const csvContent = await PhoneNumberAdminService.exportToCSV(filters);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `phone-numbers-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("Phone numbers exported");
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    }
  };

  const handleQuickSearch = () => {
    if (quickSearch.trim()) {
      setFilters({ ...filters, search: quickSearch.trim() });
      setPage(1);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleQuickSearch();
    }
  };

  const showPortingTasks = () => {
    setActiveView("porting");
    setPage(1);
  };

  const showAllNumbers = () => {
    setActiveView("all");
    setPage(1);
  };

  const handlePortingTaskUpdated = () => {
    refetchPhoneNumbers();
    refetchStats();
    setSelectedPortingTask(null);
  };

  // Get top countries for stats display
  const topCountries = stats?.by_country
    ? Object.entries(stats.by_country)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
    : [];

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Phone className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Phone Numbers</h1>
              <p className="text-genie-text-secondary">
                View all provisioned phone numbers and their associated genies
              </p>
            </div>
          </div>
          <Button onClick={handleExport} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Quick Search - Prominent placement for Twilio lookup */}
        <div className="bg-white rounded-lg p-6 mb-6 border border-genie-border">
          <h2 className="text-lg font-semibold text-genie-text-primary mb-3">Quick Lookup</h2>
          <p className="text-sm text-genie-text-secondary mb-4">
            Paste a phone number from Twilio to find which genie it belongs to
          </p>
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="e.g., +1 (781) 512-7716 or 7815127716"
                className="pl-10"
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>
            <Button onClick={handleQuickSearch}>Search</Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Total Numbers</p>
                  <p className="text-2xl font-bold text-genie-text-primary">{stats.total}</p>
                </div>
                <Phone className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <button
              type="button"
              onClick={showPortingTasks}
              className="bg-white rounded-lg p-6 border border-genie-border text-left hover:border-genie-primary transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Porting Tasks</p>
                  <p className="text-2xl font-bold text-genie-accent">{stats.portingTasks}</p>
                  <p className="text-xs text-genie-text-muted">
                    {stats.portRequested} requested, {stats.portPending} pending
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-genie-accent" />
              </div>
            </button>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Active</p>
                  <p className="text-2xl font-bold text-genie-primary">{stats.active}</p>
                </div>
                <Users className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Countries</p>
                  <p className="text-2xl font-bold text-genie-text-primary">
                    {Object.keys(stats.by_country).length}
                  </p>
                </div>
                <Globe className="h-8 w-8 text-genie-secondary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Top Countries</p>
                  <div className="flex gap-2 mt-1">
                    {topCountries.map(([code, count]) => (
                      <span key={code} className="text-sm text-genie-text-primary" title={getCountryName(code)}>
                        {getCountryFlag(code)} {count}
                      </span>
                    ))}
                  </div>
                </div>
                <MapPin className="h-8 w-8 text-genie-secondary" />
              </div>
            </div>
          </div>
        )}

        {stats && stats.portingTasks > 0 && activeView !== "porting" && (
          <button
            type="button"
            onClick={showPortingTasks}
            className="w-full mb-6 bg-white rounded-lg border border-genie-accent/30 p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-genie-accent" />
              <div>
                <p className="text-sm font-semibold text-genie-text-primary">
                  {stats.portingTasks} unresolved phone number porting {stats.portingTasks === 1 ? "task" : "tasks"}
                </p>
                <p className="text-xs text-genie-text-secondary">Open Porting Tasks to review customer requests and next steps.</p>
              </div>
            </div>
          </button>
        )}

        <div className="flex gap-2 mb-4">
          <Button variant={activeView === "all" ? "default" : "outline"} onClick={showAllNumbers}>
            All Numbers
          </Button>
          <Button variant={activeView === "porting" ? "default" : "outline"} onClick={showPortingTasks}>
            Porting Tasks
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Filter by number..."
                className="pl-10"
                value={filters.search || ""}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  setPage(1);
                }}
              />
            </div>
            <Select
              value={filters.country_code || "all"}
              onValueChange={(value) => {
                setFilters({ ...filters, country_code: value === "all" ? undefined : value });
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countryCodes?.map((code) => (
                  <SelectItem key={code} value={code}>
                    {getCountryFlag(code)} {getCountryName(code)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => {
                setFilters({ ...filters, status: value === "all" ? undefined : value });
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="in-use">In Use</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="port_requested">Port Requested</SelectItem>
                <SelectItem value="port_pending">Port Pending</SelectItem>
                <SelectItem value="released">Released</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({});
                setQuickSearch("");
                setPage(1);
              }}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Phone Numbers Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Genie</TableHead>
                  <TableHead className="hidden lg:table-cell">User</TableHead>
                  <TableHead className="hidden md:table-cell">Country</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden xl:table-cell">Provisioned</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading phone numbers...
                    </TableCell>
                  </TableRow>
                ) : phoneData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-genie-text-muted">
                      {filters.search
                        ? `No phone numbers found matching "${filters.search}"`
                        : "No phone numbers found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  phoneData?.data.map((phone: PhoneNumberWithDetails) => (
                    <TableRow key={phone.id}>
                      <TableCell>
                        <div>
                          <div className="font-mono text-genie-text-primary">
                            {formatPhoneNumber(phone.phone_number)}
                          </div>
                          {phone.friendly_name && (
                            <div className="text-xs text-genie-text-secondary">{phone.friendly_name}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-genie-text-primary font-medium">
                          {phone.agent_name || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div>
                          <div className="text-sm text-genie-text-primary">{phone.user_name || "—"}</div>
                          {phone.user_email && (
                            <CopyableEmail email={phone.user_email} className="text-xs text-genie-text-secondary" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {phone.country_code ? (
                          <span title={getCountryName(phone.country_code)}>
                            {getCountryFlag(phone.country_code)} {phone.country_code}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{getTypeBadge(phone.is_transfer, phone.metadata)}</TableCell>
                      <TableCell>{getStatusBadge(phone.status)}</TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-genie-text-secondary">
                        {new Date(phone.date_created).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {phone.metadata?.source === "ported" ? (
                          <Button size="sm" variant="outline" onClick={() => setSelectedPortingTask(phone)}>
                            Manage Porting
                          </Button>
                        ) : (
                          <span className="text-xs text-genie-text-muted">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {isLoading ? (
              <div className="text-center py-8 text-genie-text-secondary p-4">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                <p>Loading phone numbers...</p>
              </div>
            ) : phoneData?.data.length === 0 ? (
              <div className="text-center py-8 text-genie-text-muted p-4">
                {filters.search
                  ? `No phone numbers found matching "${filters.search}"`
                  : "No phone numbers found"}
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {phoneData?.data.map((phone: PhoneNumberWithDetails) => (
                  <div key={phone.id} className="border border-genie-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-mono font-medium text-genie-text-primary text-sm">
                          {formatPhoneNumber(phone.phone_number)}
                        </p>
                        {phone.friendly_name && (
                          <p className="text-xs text-genie-text-secondary">{phone.friendly_name}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {getStatusBadge(phone.status)}
                      {getTypeBadge(phone.is_transfer, phone.metadata)}
                    </div>

                    <div className="text-xs text-genie-text-secondary space-y-1">
                      <p><span className="text-genie-text-muted">Genie:</span> {phone.agent_name || "—"}</p>
                      {phone.user_name && (
                        <p><span className="text-genie-text-muted">User:</span> {phone.user_name}</p>
                      )}
                      {phone.user_email && (
                        <p className="break-all"><span className="text-genie-text-muted">Email:</span> <CopyableEmail email={phone.user_email} /></p>
                      )}
                      {phone.country_code && (
                        <p><span className="text-genie-text-muted">Country:</span> {getCountryFlag(phone.country_code)} {getCountryName(phone.country_code)}</p>
                      )}
                      <p><span className="text-genie-text-muted">Provisioned:</span> {new Date(phone.date_created).toLocaleDateString()}</p>
                      {phone.metadata?.source === "ported" && (
                        <Button size="sm" variant="outline" className="mt-2" onClick={() => setSelectedPortingTask(phone)}>
                          Manage Porting
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {phoneData && phoneData.count > 50 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, phoneData.count)} of{" "}
                {phoneData.count} numbers
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * 50 >= phoneData.count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <PortingTaskDrawer
        phone={selectedPortingTask}
        isOpen={!!selectedPortingTask}
        onClose={() => setSelectedPortingTask(null)}
        onUpdated={handlePortingTaskUpdated}
      />
    </div>
  );
};

export default PhoneNumbersPage;
